import React, { Fragment } from 'react'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'
import styles from './Collections.scss'
import { CollectionCard } from './CollectionCard'
import { collectionsMessages } from './intlMessages'
import * as userActions from 'lib/api/currentUser'
import { showModal } from 'lib/modalUtils'
import { promiseMapAll } from 'lib/utils'
import { fm, commonVerbs, Msg, featureNames } from 'lib/localization'
import { collectionActions, collectionSelectors } from 'data/resources/collections'
import { privacyCodeSelectors } from 'data/resources/privacyCodes'
import { userSelectors } from 'data/resources/users'
import { Card } from 'common/styleguide/Card'
import { CTA } from 'common/styleguide/CTA'
import { RadioGroup } from 'common/styleguide/RadioGroup'
import { StandardLayout } from 'common/layouts/StandardLayout'
import popoverMenuStyles from 'common/styleguide/PopoverMenu/PopoverMenu.scss'
import { CREATE_EDIT_COLLECTION_DIALOG } from 'common/modals/CreateEditCollection'
import { SHARE_MODAL } from 'common/modals/ShareModal'
import { withCollapseState } from 'common/layouts/SideNavLayout/SideNavContexts'
import { PopoverFilter } from 'common/PopoverFilter'
import { IeNudgeBar } from 'common/IeNudgeBar'
import { Loadable } from 'common/Loadable'
import { OrderedMap } from 'immutable'
import Helmet from 'react-helmet'
import { FormattedMessage as T, injectIntl, defineMessages } from 'react-intl'
import cx from 'classnames'

const ownershipMessages = defineMessages({
  all: {
    id: 'Collections.allCollections',
    defaultMessage: 'All Collections'
  },
  yours: {
    id: 'Collections.yourCollections',
    defaultMessage: 'Yours'
  },
  followed: {
    id: 'Collections.followedCollections',
    defaultMessage: 'Followed'
  }
})

const ownershipFilterOpts = intl => OrderedMap({
  all: fm(intl, ownershipMessages.all),
  yours: fm(intl, ownershipMessages.yours),
  followed: fm(intl, ownershipMessages.followed)
})

const filterCollections = (collections, ownership, cuid) => {
  switch (ownership) {
    case 'yours':
      return collections.filter(c => c.userId === cuid)
    case 'followed':
      return collections.filter(c => c.userId !== cuid)
    default:
      return collections
  }
}

class _Collections extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      ownership: 'all'
    }
  }

  onOwnershipChange = (ownership) => {
    this.setState({ ownership })
  }

  render () {
    const {
      readOnly,
      collections,
      currentUser,
      setPreference,
      intl,
      collapseState,
      collectionsPrivacyCode,
      user
    } = this.props
    const {
      ownership
    } = this.state
    const filteredCollections = filterCollections(collections, ownership, currentUser.id)
    const disableFilter = !collections.find(c => c.userId !== currentUser.id)
    const isMobile = collapseState === 'viewport'

    return (
      <Fragment>
        <Helmet title={`${fm(intl, featureNames.collections)} · Ride with GPS `} />
        <IeNudgeBar currentUser={currentUser} setPreference={setPreference} />
        <div className={cx(styles.wrap, isMobile && styles.collapsed)}>
          {collections.size > 0 ? <Fragment>
            <h2 className={styles.collectionsHeader} data-heading-alt>
              <Msg m={featureNames.collections} />
              {readOnly && ` · ${user.name}`}
            </h2>
            {!readOnly && <div className={cx(styles.collectionsTools, isMobile && styles.collapsed)}>
              <PopoverFilter
                hidePopoverLabel
                disabled={disableFilter}
                label={ownershipFilterOpts(intl).get(ownership)}
                className={styles.ownershipPopover}>
                <ul className={popoverMenuStyles.PopoverMenu}>
                  <RadioGroup
                    style={{ margin: '0 0' }}
                    horizontal
                    options={ownershipFilterOpts(intl)}
                    selected={ownership}
                    radioClass={styles.radioClass}
                    onChange={this.onOwnershipChange} />
                </ul>
              </PopoverFilter>
              <div style={{ display: 'flex' }}>
                <CTA
                  secondary
                  className={styles.shareCTA}
                  onClick={() => showModal(SHARE_MODAL, {
                    overlayTitle: <T id='Collections.shareYourCollections' defaultMessage='Share your collections' />,
                    objectType: 'collections',
                    objectUrl: `/users/${currentUser.id}/collections?privacy_code=${collectionsPrivacyCode.code}`
                  })}>
                  {fm(intl, commonVerbs.share)}
                </CTA>
                <CTA
                  secondary
                  margins={false}
                  className={styles.newCTA}
                  onClick={() => showModal(CREATE_EDIT_COLLECTION_DIALOG, {
                    modalTitle: fm(intl, collectionsMessages.newCollection)
                  })}>
                  {fm(intl, collectionsMessages.newCollection)}
                </CTA>
              </div>
            </div>}
            <div className={cx(styles.collectionCards, isMobile && styles.collapsed)}>
              {filteredCollections.toIndexedSeq().map(c => <CollectionCard key={c.id} collection={c} currentUser={currentUser} isMobile={isMobile} />)}
            </div>
          </Fragment>
            : <NoCollections {...this.props} />}
        </div>
      </Fragment>
    )
  }
}

const NoCollections = ({ intl }) => (
  <div className={styles.empty}>
    <h2 data-heading-alt>
      <T id='Collections.emptyHeader' defaultMessage='Welcome to Collections' />
    </h2>
    <Card noPadding className={styles.emptyCard} onClick={() => showModal(CREATE_EDIT_COLLECTION_DIALOG, {
      modalTitle: fm(intl, collectionsMessages.newCollection)
    })}>
      <div className={styles.emptyCardTop}>
        <T id='Collections.emptyCardTop' defaultMessage='Create Collection' />
      </div>
      <div className={styles.emptyCardBottom}>
        <T
          id='Collections.emptyCardBottom'
          defaultMessage='Collections are the best way to organize your routes and rides, and to share them with others.' />
      </div>
    </Card>
  </div>
)

const CollectionsWithCollapseState = withCollapseState(_Collections)
export const I18nCollections = injectIntl(CollectionsWithCollapseState) // export for tests

const loadInitialResources = ({ loadCollections, params, location }) => {
  return promiseMapAll({
    _: loadCollections({ extras: 'privacy_code,user', userId: params.user_id, privacyCode: location.query.privacy_code })
  })
}

const LoadableCollections = (props) => {
  return <Loadable
    {...props}
    component={I18nCollections}
    loadingText={<T id='Collections.loadableText' defaultMessage='Loading your collections' />}
    resourceLoaded={() => loadInitialResources(props)} />
}

const mapStateToProps = (state, ownProps) => ({
  currentUser: state.currentUser,
  user: ownProps.user || userSelectors.find(state, +ownProps.params.user_id),
  collections: collectionSelectors.allForUser(state),
  collectionsPrivacyCode: privacyCodeSelectors.byParentWithScope(state, 'User', state.currentUser.id, 'collections')
})

const mapDispatchToProps = {
  setPreference: userActions.setPreference,
  loadCollections: collectionActions.list
}

export const Collections = withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableCollections))

export const CollectionsPage = () => {
  return (
    <StandardLayout greyBg>
      <Collections readOnly />
    </StandardLayout>
  )
}
